import { useEffect, useState, useRef, Fragment as ReactFragment } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, CATEGORIES } from "@/lib/api";
import { toast } from "sonner";
import { ImageUpload, toAbsoluteUrl, uploadFileToServer } from "@/components/ImageUpload";
import { isAllowedImage } from "@/lib/api";
import ContentTab from "@/pages/admin/ContentTab";
import ChatsTab from "@/pages/admin/ChatsTab";
import PromosTab from "@/pages/admin/PromosTab";
import PagesTab from "@/pages/admin/PagesTab";
import { SessionCalendar } from "@/components/SessionCalendar";
import { LogOut, Plus, Trash2, Copy, Upload, LayoutDashboard, Calendar, MessageSquare, Image, Settings as SettingsIcon, Ticket, PenTool, Building, FileText, ArrowUp, ArrowDown, Eye, EyeOff, Star } from "lucide-react";

const TABS = [
  { id: "workshops", label: "Workshops", icon: Calendar },
  { id: "bookings", label: "Bookings", icon: Ticket },
  { id: "private_events", label: "Private Events", icon: MessageSquare },
  { id: "corporate_quotes", label: "Corporate Quotes", icon: Building },
  { id: "custom_rugs", label: "Custom Rugs", icon: PenTool },
  { id: "gallery", label: "Gallery", icon: Image },
  { id: "content", label: "Website Content", icon: FileText },
  { id: "chats", label: "Chats", icon: MessageSquare },
  { id: "promos", label: "Promo Codes", icon: Ticket },
  { id: "pages", label: "Pages & Offerings", icon: FileText },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState(params.get("tab") || "workshops");
  const initialSid = params.get("sid");

  useEffect(() => { if (user === null) nav("/admin/login"); }, [user, nav]);
  if (!user) return <div className="min-h-screen grid place-items-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-cream flex flex-col md:flex-row" data-testid="admin-dashboard">
      <aside className="w-full md:w-64 bg-white border-b md:border-b-0 md:border-r border-cream-alt py-4 md:py-8 px-4 md:sticky md:top-0 md:h-screen">
        <div className="px-3 mb-3 md:mb-8">
          <div className="font-serif font-bold text-lg">That's Tuft</div>
          <div className="text-xs text-charcoal-muted">Admin</div>
        </div>
        <nav className="flex md:block gap-1 overflow-x-auto md:space-y-1 pb-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`shrink-0 md:w-full flex items-center gap-2 md:gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium transition-colors whitespace-nowrap ${tab === t.id ? "bg-terracotta/10 text-terracotta" : "text-charcoal hover:bg-cream-alt"}`}>
              <t.icon size={18} strokeWidth={1.5}/> {t.label}
            </button>
          ))}
        </nav>
        <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout" className="hidden md:flex mt-8 w-full items-center gap-3 px-3 py-2.5 rounded-2xl text-sm text-charcoal-muted hover:bg-cream-alt"><LogOut size={16}/> Logout</button>
      </aside>
      <main className="flex-1 min-w-0 p-4 sm:p-8 lg:p-12">
        {tab === "workshops" && <WorkshopsTab/>}
        {tab === "bookings" && <ListTab title="Bookings" endpoint="/admin/bookings" actions cols={["created_at", "workshop_name", "date", "name", "email", "guests", "design_summary", "promo_code", "discount", "tip", "amount", "status"]}/>}
        {tab === "private_events" && <InquiriesTab title="Private Event Requests" endpoint="/admin/private-events" collection="private_events" cols={["created_at", "name", "email", "event_type", "guests", "preferred_date", "status"]}/>}
        {tab === "corporate_quotes" && <InquiriesTab title="Corporate Quotes" endpoint="/admin/corporate-quotes" collection="corporate_quotes" cols={["created_at", "organization", "contact_name", "email", "event_type", "experience", "attendance", "custom_quote_total", "status"]}/>}
        {tab === "custom_rugs" && <InquiriesTab title="Custom Rug Inquiries" endpoint="/admin/custom-rugs" collection="custom_rugs" cols={["created_at", "name", "email", "width", "height", "estimate", "status"]}/>}
        {tab === "gallery" && <GalleryTab/>}
        {tab === "content" && <ContentTab/>}
        {tab === "chats" && <ChatsTab initialSid={initialSid}/>}
        {tab === "promos" && <PromosTab/>}
        {tab === "pages" && <PagesTab/>}
        {tab === "settings" && <SettingsTab/>}
      </main>
    </div>
  );
}

function WorkshopsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/workshops").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!window.confirm("Delete workshop?")) return; await api.delete(`/admin/workshops/${id}`); load(); };
  const dupe = async (id) => { await api.post(`/admin/workshops/${id}/duplicate`, { days_offset: 7 }); toast.success("Duplicated to next week (saved as draft)"); load(); };
  const dupeWeekly = async (id) => {
    const raw = window.prompt("Repeat this session weekly for how many weeks?", "4");
    if (!raw) return;
    const weeks = parseInt(raw, 10);
    if (!weeks || weeks < 1) return toast.error("Enter a number between 1 and 52");
    await api.post(`/admin/workshops/${id}/duplicate-weekly`, { weeks });
    toast.success(`Created ${weeks} weekly copies (saved as drafts)`);
    load();
  };
  const toggle = async (w, field) => { await api.put(`/admin/workshops/${w.id}`, { [field]: !w[field] }); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl font-bold">Workshops</h1>
          <p className="text-charcoal-muted text-sm">Add, edit, duplicate, hide or delete workshops.</p>
        </div>
        <button onClick={() => setEditing({})} data-testid="add-workshop-btn" className="rounded-full bg-terracotta text-white px-5 py-2.5 font-semibold shadow-button inline-flex items-center gap-2"><Plus size={16}/> New Workshop</button>
      </div>
      {editing !== null && <WorkshopForm existing={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)}/>}
      <div className="mt-6 bg-white rounded-3xl shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-alt text-charcoal">
            <tr>{["Name", "Category", "Date", "Time", "Price", "Booked/Cap", "Status", "Actions"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.id} className="border-t border-cream-alt hover:bg-cream/40" data-testid={`admin-workshop-row-${w.id}`}>
                <td className="px-4 py-3 font-medium">{w.name}</td>
                <td className="px-4 py-3">{w.category}</td>
                <td className="px-4 py-3">{w.date}</td>
                <td className="px-4 py-3">{w.start_time}</td>
                <td className="px-4 py-3">${w.price}</td>
                <td className="px-4 py-3">{w.booked}/{w.capacity}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    <Tag label="Feat" on={w.featured} onClick={() => toggle(w, "featured")}/>
                    <Tag label="Sold" on={w.sold_out} onClick={() => toggle(w, "sold_out")}/>
                    <Tag label="Draft" on={w.draft} onClick={() => toggle(w, "draft")}/>
                    <Tag label="Hide" on={w.hidden} onClick={() => toggle(w, "hidden")}/>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditing(w)} data-testid={`edit-${w.id}`} className="px-3 py-1.5 rounded-full bg-cream-alt text-xs">Edit</button>
                    <button onClick={() => dupe(w.id)} title="Duplicate to next week" data-testid={`dup-${w.id}`} className="p-1.5 rounded-full bg-cream-alt"><Copy size={13}/></button>
                    <button onClick={() => dupeWeekly(w.id)} title="Repeat weekly for N weeks" data-testid={`dup-weekly-${w.id}`} className="px-2 py-1 rounded-full bg-ochre/25 text-charcoal text-[10px] font-bold">×N</button>
                    <button onClick={() => remove(w.id)} title="Delete" data-testid={`del-${w.id}`} className="p-1.5 rounded-full bg-red-50 text-red-600"><Trash2 size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tag({ label, on, onClick }) {
  return <button onClick={onClick} className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${on ? "bg-terracotta text-white" : "bg-cream-alt text-charcoal-muted"}`}>{label}</button>;
}

function WorkshopForm({ existing, onDone, onCancel }) {
  const [form, setForm] = useState({ name: "", category: "rug_tufting", description: "", photo_url: "", date: "", start_time: "18:00", duration_minutes: 120, price: 0, sale_price: null, sale_active: false, age_requirement: "16+", capacity: 10, featured: false, is_theme: false, design_instructions: "", allow_addons: true, allowed_size_ids: [], theme_designs: [], sessions: [], ...existing });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, sale_price: form.sale_price === "" || form.sale_price == null ? null : Number(form.sale_price) };
      if (existing?.id) await api.put(`/admin/workshops/${existing.id}`, payload);
      else await api.post("/admin/workshops", payload);
      toast.success("Saved"); onDone();
    } catch (e) { toast.error("Save failed"); }
  };
  const updateDesigns = (v) => setForm({...form, theme_designs: v});
  const addDesign = () => updateDesigns([...(form.theme_designs || []), { id: `d-${Date.now()}`, title: "", image_url: "", description: "", hidden: false, unavailable: false, order: (form.theme_designs || []).length }]);
  const patchDesign = (i, patch) => { const c = [...(form.theme_designs || [])]; c[i] = { ...c[i], ...patch }; updateDesigns(c); };
  const removeDesign = (i) => { const c = [...(form.theme_designs || [])]; c.splice(i, 1); updateDesigns(c); };
  const moveDesign = (i, dir) => { const c = [...(form.theme_designs || [])]; const j = i + dir; if (j < 0 || j >= c.length) return; [c[i], c[j]] = [c[j], c[i]]; c.forEach((it, idx) => it.order = idx); updateDesigns(c); };
  const updateSessions = (v) => setForm((f) => ({...f, sessions: v}));
  return (
    <form onSubmit={submit} className="bg-white rounded-3xl p-6 shadow-card grid md:grid-cols-3 gap-3" data-testid="workshop-form">
      <div className="md:col-span-3 sticky top-0 z-10 -mx-6 -mt-6 px-6 py-3 bg-white/95 backdrop-blur border-b border-cream-alt flex items-center justify-between rounded-t-3xl">
        <div className="font-serif text-lg font-semibold">{existing?.id ? `Edit: ${form.name || "Workshop"}` : "New Workshop"}</div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} data-testid="cancel-workshop-btn-top" className="rounded-full bg-cream-alt px-4 py-2 text-sm font-semibold">Cancel</button>
          <button type="submit" data-testid="save-workshop-btn-top" className="rounded-full bg-terracotta text-white px-5 py-2 text-sm font-semibold shadow-button">Save Workshop</button>
        </div>
      </div>
      <F label="Name" v={form.name} on={(v) => setForm({...form, name: v})} req/>
      <Sel label="Category" v={form.category} on={(v) => setForm({...form, category: v})} opts={CATEGORIES.slice(1).map((c) => c.id)}/>
      <div className="md:col-span-3">
        <label className="text-xs font-medium">Photo</label>
        <div className="mt-1"><ImageUpload value={form.photo_url} onChange={(v) => setForm({...form, photo_url: v})} testid="wf-photo-upload"/></div>
      </div>
      <F label="Default Date" v={form.date} on={(v) => setForm({...form, date: v})} type="date"/>
      <F label="Default Start Time" v={form.start_time} on={(v) => setForm({...form, start_time: v})} type="time"/>
      <F label="Duration (min)" v={form.duration_minutes} on={(v) => setForm({...form, duration_minutes: Number(v)})} type="number"/>
      <F label="Age Req" v={form.age_requirement} on={(v) => setForm({...form, age_requirement: v})}/>
      <F label="Default Capacity" v={form.capacity} on={(v) => setForm({...form, capacity: Number(v)})} type="number"/>
      <F label="Regular Price ($)" v={form.price} on={(v) => setForm({...form, price: Number(v)})} type="number" name="wf-price"/>
      <F label="Sale Price ($, optional)" v={form.sale_price ?? ""} on={(v) => setForm({...form, sale_price: v === "" ? null : Number(v)})} type="number" name="wf-sale-price"/>
      <div className="md:col-span-3">
        <label className="text-xs font-medium">Square Payment Link (optional)</label>
        <input data-testid="wf-square-link" value={form.square_link || ""} onChange={(e) => setForm({...form, square_link: e.target.value})} placeholder="https://square.link/u/…  — customers see a “Pay with Square” button after booking" className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-2 text-sm"/>
      </div>
      <label className="text-xs flex items-center gap-2 mt-6">
        <input type="checkbox" data-testid="wf-sale-active" checked={!!form.sale_active} onChange={(e) => setForm({...form, sale_active: e.target.checked})}/>
        Sale price active
      </label>
      <label className="text-xs flex items-center gap-2 mt-6">
        <input type="checkbox" data-testid="wf-is-theme" checked={!!form.is_theme} onChange={(e) => setForm({...form, is_theme: e.target.checked})}/>
        Is a Theme
      </label>
      <label className="text-xs flex items-center gap-2 mt-6">
        <input type="checkbox" data-testid="wf-allow-sip" checked={form.allow_addons !== false} onChange={(e) => setForm({...form, allow_addons: e.target.checked})}/>
        Allow Sip add-on
      </label>
      <label className="text-xs flex items-center gap-2 mt-6">
        <input type="checkbox" data-testid="wf-taxable" checked={form.taxable !== false} onChange={(e) => setForm({...form, taxable: e.target.checked})}/>
        Taxable (sales tax applies)
      </label>
      <div className="md:col-span-3">
        <label className="text-sm font-medium">Description</label>
        <textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-2 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
      </div>
      <div className="md:col-span-3">
        <label className="text-sm font-medium">Design Instructions (leave blank to use global default)</label>
        <textarea data-testid="wf-design-instructions" rows={4} value={form.design_instructions || ""} onChange={(e) => setForm({...form, design_instructions: e.target.value})} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-2 focus:ring-2 focus:ring-terracotta focus:outline-none" placeholder="Optional per-workshop design guidance"/>
      </div>
      <div className="md:col-span-3 rounded-2xl bg-cream-alt/50 p-4" data-testid="sessions-editor">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-serif text-lg">Dates & Times</div>
            <div className="text-xs text-charcoal-muted">Click dates on the calendar to select them, set one start time and capacity, then add them all at once. Customers will see each date as its own bookable slot. Leave empty to use the default date/time above.</div>
          </div>
        </div>
        <SessionCalendar sessions={form.sessions || []} onChange={updateSessions} defaultTime={form.start_time} defaultCapacity={form.capacity}/>
      </div>
      {form.is_theme && (
        <div className="md:col-span-3 rounded-2xl bg-cream-alt/50 p-4" data-testid="theme-designs-editor">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-serif text-lg">Theme Designs</div>
              <div className="text-xs text-charcoal-muted">Upload rug design images customers can visually pick from during booking. Hide or mark unavailable when out of stock.</div>
            </div>
            <button type="button" onClick={addDesign} data-testid="add-theme-design" className="rounded-full bg-terracotta text-white text-xs font-semibold px-4 py-2 inline-flex items-center gap-1"><Plus size={12}/> Add Design</button>
          </div>
          <div className="mt-4 space-y-3">
            {(form.theme_designs || []).map((d, i) => (
              <div key={d.id || i} className="bg-white rounded-2xl p-3" data-testid={`theme-design-item-${i}`}>
                <div className="grid md:grid-cols-2 gap-2">
                  <label className="text-xs">Title
                    <input value={d.title || ""} onChange={(e) => patchDesign(i, {title: e.target.value})} className="mt-1 w-full rounded-xl border border-cream-alt px-3 py-2" data-testid={`td-title-${i}`}/>
                  </label>
                  <label className="text-xs">Description
                    <input value={d.description || ""} onChange={(e) => patchDesign(i, {description: e.target.value})} className="mt-1 w-full rounded-xl border border-cream-alt px-3 py-2"/>
                  </label>
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium">Design image</label>
                    <div className="mt-1"><ImageUpload value={d.image_url} onChange={(v) => patchDesign(i, {image_url: v})} testid={`td-image-upload-${i}`}/></div>
                  </div>
                  <div className="md:col-span-2 flex items-center gap-3 text-xs">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!d.hidden} onChange={(e) => patchDesign(i, {hidden: e.target.checked})}/> Hidden</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!d.unavailable} onChange={(e) => patchDesign(i, {unavailable: e.target.checked})}/> Sold out</label>
                    <button type="button" onClick={() => moveDesign(i, -1)} className="px-2 py-1 rounded-full bg-cream-alt">↑</button>
                    <button type="button" onClick={() => moveDesign(i, 1)} className="px-2 py-1 rounded-full bg-cream-alt">↓</button>
                    <button type="button" onClick={() => removeDesign(i)} data-testid={`td-remove-${i}`} className="ml-auto px-3 py-1 rounded-full bg-red-50 text-red-600 inline-flex items-center gap-1"><Trash2 size={11}/> Remove</button>
                  </div>
                </div>
              </div>
            ))}
            {(!form.theme_designs || form.theme_designs.length === 0) && <div className="text-xs text-charcoal-muted">No designs yet. Add one so customers can pick their rug during booking.</div>}
          </div>
        </div>
      )}
      <div className="md:col-span-3 flex gap-3">
        <button type="submit" data-testid="save-workshop-btn" className="rounded-full bg-terracotta text-white px-6 py-2.5 font-semibold">Save</button>
        <button type="button" onClick={onCancel} className="rounded-full bg-cream-alt px-6 py-2.5 font-semibold">Cancel</button>
      </div>
    </form>
  );
}

function InquiriesTab({ title, endpoint, collection, cols }) {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  const load = () => api.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [endpoint]);
  const act = async (id, action) => { await api.post(`/admin/inquiry/${collection}/${id}/${action}`); load(); toast.success(`Marked ${action}`); };
  const sendDeposit = async (id) => {
    const link = window.prompt("Paste the Square payment link for this deposit (create it in your Square account):", "");
    if (!link) return;
    try {
      await api.post(`/admin/inquiry/${collection}/${id}/send-deposit`, { square_link: link.trim() });
      toast.success("Square deposit link sent to customer");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send deposit link"); }
  };
  const setCorpQuote = async (id) => {
    const raw = window.prompt("Enter approved custom quote total ($):", "");
    if (!raw) return;
    const amt = parseFloat(raw);
    if (!amt || amt <= 0) return toast.error("Amount must be greater than 0");
    try {
      await api.put(`/admin/corporate-quotes/${id}/quote`, { custom_quote_total: amt });
      toast.success(`Quote set — $${amt.toFixed(2)}. Now send the deposit link.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save quote"); }
  };
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  return (
    <div>
      <h1 className="font-serif text-3xl font-bold">{title}</h1>
      <div className="mt-6 bg-white rounded-3xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream-alt"><tr><th className="w-8"></th>{cols.map((c) => <th key={c} className="text-left px-4 py-3">{c}</th>)}{collection === "private_events" && <th className="text-left px-4 py-3">Total</th>}<th className="text-left px-4 py-3">Actions</th></tr></thead>
          <tbody>
            {items.map((it) => (
              <ReactFragment key={it.id}>
                <tr className="border-t border-cream-alt align-top" data-testid={`inquiry-row-${it.id}`}>
                  <td className="px-2 py-3"><button onClick={() => toggle(it.id)} data-testid={`inquiry-expand-${it.id}`} className="w-6 h-6 rounded-full bg-cream-alt text-xs">{expanded[it.id] ? "−" : "+"}</button></td>
                  {cols.map((c) => <td key={c} className="px-4 py-3">{formatCell(it[c])}</td>)}
                  {collection === "private_events" && <td className="px-4 py-3">{it.breakdown ? `$${(it.breakdown.total || 0).toFixed(0)} / dep $${(it.breakdown.deposit_amount || 0).toFixed(0)}` : "—"}</td>}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => act(it.id, "approve")} data-testid={`approve-${it.id}`} className="px-3 py-1 rounded-full bg-forest text-white text-xs">Approve</button>
                      <button onClick={() => act(it.id, "decline")} data-testid={`decline-${it.id}`} className="px-3 py-1 rounded-full bg-red-50 text-red-600 text-xs">Decline</button>
                      {collection === "private_events" && (
                        <button onClick={() => sendDeposit(it.id)} data-testid={`send-deposit-${it.id}`} className="px-3 py-1 rounded-full bg-terracotta text-white text-xs">Send Deposit Link</button>
                      )}
                      {collection === "corporate_quotes" && (
                        <>
                          <button onClick={() => setCorpQuote(it.id)} data-testid={`set-quote-${it.id}`} className="px-3 py-1 rounded-full bg-ochre text-charcoal text-xs font-semibold">{it.custom_quote_total ? `Edit Quote ($${Number(it.custom_quote_total).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})})` : "Set Custom Quote"}</button>
                          {it.custom_quote_total > 0 && <button onClick={() => sendDeposit(it.id)} data-testid={`send-deposit-${it.id}`} className="px-3 py-1 rounded-full bg-terracotta text-white text-xs">Send Deposit Link</button>}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {expanded[it.id] && (
                  <tr key={`${it.id}-details`} className="bg-cream/40">
                    <td colSpan={cols.length + (collection === "private_events" ? 3 : 2)} className="px-6 py-4">
                      <InquiryDetails it={it} collection={collection}/>
                    </td>
                  </tr>
                )}
              </ReactFragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InquiryDetails({ it, collection }) {
  if (collection === "private_events") {
    const isMobile = (it.location || "").toLowerCase() === "mobile";
    return (
      <div className="grid md:grid-cols-2 gap-4 text-xs" data-testid={`inquiry-details-${it.id}`}>
        <Detail label="Phone" v={it.phone}/>
        <Detail label="Alternate date" v={it.alternate_date}/>
        <Detail label="Preferred time" v={it.preferred_time}/>
        <Detail label="Age range" v={it.age_range}/>
        <Detail label="Budget" v={it.budget}/>
        <Detail label="Location" v={it.location}/>
        <Detail label="Design theme" v={it.theme_pref}/>
        <Detail label="Design notes" v={it.theme_notes} wide/>
        {isMobile && (
          <>
            <Detail label="Venue" v={it.venue_name}/>
            <Detail label="Address" v={[it.address, it.city, it.state, it.zip_code].filter(Boolean).join(", ")}/>
            <Detail label="Parking / loading" v={it.parking_info} wide/>
            <Detail label="Access / stairs / elevator" v={it.access_info} wide/>
            <Detail label="Setup restrictions" v={it.setup_restrictions} wide/>
            <Detail label="Additional location notes" v={it.location_notes} wide/>
          </>
        )}
        <Detail label="General notes" v={it.notes} wide/>
        <Detail label="Add-ons" v={(it.addon_ids || []).join(", ") || "—"}/>
        <Detail label="Rug size id" v={it.rug_size_id}/>
        <Detail label="Deposit terms accepted" v={it.event_terms_accepted ? "yes" : "no"}/>
      </div>
    );
  }
  return (
    <pre className="text-xs whitespace-pre-wrap text-charcoal-muted" data-testid={`inquiry-details-${it.id}`}>{JSON.stringify(it, null, 2)}</pre>
  );
}

function Detail({ label, v, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <div className="text-charcoal-muted uppercase tracking-widest text-[10px] font-bold">{label}</div>
      <div className="mt-0.5 whitespace-pre-wrap">{v ? String(v) : "—"}</div>
    </div>
  );
}

function ListTab({ title, endpoint, cols, actions }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [endpoint]);
  const setStatus = async (id, status) => {
    if (status === "cancelled" && !window.confirm("Cancel this booking and release the spot?")) return;
    try { await api.post(`/admin/bookings/${id}/status`, { status }); toast.success(status === "paid" ? "Marked paid — confirmation sent" : `Status: ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  return (
    <div>
      <h1 className="font-serif text-3xl font-bold">{title}</h1>
      {actions && <p className="text-charcoal-muted text-sm mt-1">Bookings stay “pending_payment” until you confirm the Square payment and mark them paid.</p>}
      <div className="mt-6 bg-white rounded-3xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream-alt"><tr>{cols.map((c) => <th key={c} className="text-left px-4 py-3">{c}</th>)}{actions && <th className="text-left px-4 py-3">Actions</th>}</tr></thead>
          <tbody>
            {items.map((it, i) => (<tr key={it.id || i} className="border-t border-cream-alt" data-testid={`booking-row-${it.id}`}>{cols.map((c) => <td key={c} className="px-4 py-3">{formatCell(it[c])}</td>)}
              {actions && <td className="px-4 py-3"><div className="flex gap-2">
                {it.status !== "paid" && it.status !== "cancelled" && <button onClick={() => setStatus(it.id, "paid")} data-testid={`mark-paid-${it.id}`} className="px-3 py-1 rounded-full bg-forest text-white text-xs">Mark Paid</button>}
                {it.status !== "cancelled" && <button onClick={() => setStatus(it.id, "cancelled")} data-testid={`cancel-booking-${it.id}`} className="px-3 py-1 rounded-full bg-red-50 text-red-600 text-xs">Cancel</button>}
              </div></td>}
            </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatCell(v) {
  if (v == null) return "—";
  if (typeof v === "number") return v.toString();
  if (typeof v === "string" && v.length > 40) return v.slice(0, 40) + "…";
  return String(v);
}

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ url: "", category: "workshops", caption: "" });
  const load = () => api.get("/admin/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const patch = async (g, body) => { await api.put(`/admin/gallery/${g.id}`, body); load(); };
  const move = async (g, dir) => {
    const same = items.filter((x) => x.category === g.category);
    const i = same.findIndex((x) => x.id === g.id); const j = i + dir;
    if (j < 0 || j >= same.length) return;
    const reordered = [...same]; [reordered[i], reordered[j]] = [reordered[j], reordered[i]];
    await Promise.all(reordered.map((x, k) => api.put(`/admin/gallery/${x.id}`, { order: k })));
    load();
  };
  const setFeatured = async (g) => { const others = items.filter((x) => x.category === g.category && x.featured && x.id !== g.id); await Promise.all(others.map((x) => api.put(`/admin/gallery/${x.id}`, { featured: false }))); await patch(g, { featured: !g.featured }); };
  const add = async (e) => { e.preventDefault(); if (!f.url) return toast.error("Please add or upload an image first"); await api.post("/admin/gallery", f); setF({ url: "", category: "workshops", caption: "" }); load(); toast.success("Added"); };
  const [bulkBusy, setBulkBusy] = useState(0);
  const addMany = async (e) => {
    const files = Array.from(e.target.files || []).filter(isAllowedImage);
    e.target.value = "";
    if (!files.length) return toast.error("Choose HEIC, JPG or PNG images");
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setBulkBusy(i + 1);
      try { const url = await uploadFileToServer(files[i]); await api.post("/admin/gallery", { url, category: f.category, caption: f.caption }); ok++; }
      catch { toast.error(`Failed: ${files[i].name}`); }
    }
    setBulkBusy(0); load(); toast.success(`Added ${ok} image${ok === 1 ? "" : "s"} to ${f.category.replace(/_/g, " ")}`);
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/gallery/${id}`); load(); };
  return (
    <div>
      <h1 className="font-serif text-3xl font-bold">Gallery</h1>
      <form onSubmit={add} className="mt-6 bg-white rounded-3xl p-6 shadow-card grid md:grid-cols-2 gap-4" data-testid="gallery-add-form">
        <div>
          <label className="text-xs font-medium">Image</label>
          <div className="mt-1"><ImageUpload value={f.url} onChange={(v) => setF({...f, url: v})} testid="gallery-image-upload"/></div>
        </div>
        <div className="space-y-3">
          <Sel label="Category" v={f.category} on={(v) => setF({...f, category: v})} opts={["workshops", "private_parties", "corporate", "custom_rugs", "tuft_buddies", "mobile"]}/>
          <F label="Caption" v={f.caption} on={(v) => setF({...f, caption: v})}/>
          <button type="submit" className="rounded-full bg-terracotta text-white font-semibold px-6 py-2.5">Add to Gallery</button>
          <label className="block">
            <span className="text-xs font-medium">Or add several at once (uses the category/caption above)</span>
            <span className="mt-1 flex items-center gap-2">
              <input type="file" multiple accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif,.jpg,.jpeg,.png" onChange={addMany} disabled={!!bulkBusy} data-testid="gallery-multi-file" className="text-xs"/>
              {bulkBusy > 0 && <span className="text-xs text-charcoal-muted" data-testid="gallery-multi-progress">Uploading {bulkBusy}…</span>}
            </span>
          </label>
        </div>
      </form>
      <p className="mt-6 text-xs text-charcoal-muted">Tip: photos in the <b>custom rugs</b> category power the Custom Rugs page showcase — star one as the hero image.</p>
      <div className="mt-3 grid sm:grid-cols-2 md:grid-cols-4 gap-4" data-testid="gallery-grid">
        {items.map((g) => (
          <div key={g.id} className={`bg-white rounded-3xl shadow-card overflow-hidden ${g.hidden ? "opacity-60" : ""}`} data-testid={`gallery-item-${g.id}`}>
            <div className="relative">
              <img src={toAbsoluteUrl(g.url)} alt="" className="h-40 w-full object-cover"/>
              {g.featured && <span className="absolute top-2 left-2 rounded-full bg-terracotta text-white text-[10px] font-bold px-2 py-0.5">Featured</span>}
              {g.hidden && <span className="absolute top-2 right-2 rounded-full bg-charcoal text-cream text-[10px] font-bold px-2 py-0.5">Hidden</span>}
            </div>
            <div className="p-3 text-xs">
              <div className="flex items-center justify-between"><span>{g.category.replace(/_/g, " ")}</span><span className="text-charcoal-muted truncate max-w-[50%]">{g.caption}</span></div>
              <div className="mt-2 flex items-center gap-1">
                <button onClick={() => move(g, -1)} title="Move up" data-testid={`gallery-up-${g.id}`} className="p-1.5 rounded-full hover:bg-cream-alt"><ArrowUp size={13}/></button>
                <button onClick={() => move(g, 1)} title="Move down" data-testid={`gallery-down-${g.id}`} className="p-1.5 rounded-full hover:bg-cream-alt"><ArrowDown size={13}/></button>
                <button onClick={() => patch(g, { hidden: !g.hidden })} title={g.hidden ? "Show" : "Hide"} data-testid={`gallery-toggle-${g.id}`} className="p-1.5 rounded-full hover:bg-cream-alt">{g.hidden ? <EyeOff size={13}/> : <Eye size={13}/>}</button>
                <button onClick={() => setFeatured(g)} title="Featured / hero" data-testid={`gallery-feature-${g.id}`} className={`p-1.5 rounded-full hover:bg-cream-alt ${g.featured ? "text-terracotta" : ""}`}><Star size={13} fill={g.featured ? "currentColor" : "none"}/></button>
                <button onClick={() => del(g.id)} data-testid={`gallery-delete-${g.id}`} className="ml-auto p-1.5 rounded-full text-red-600 hover:bg-red-50"><Trash2 size={13}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState({});
  const load = () => api.get("/admin/settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  const save = async (key, value) => { await api.put(`/admin/settings/${key}`, { value }); toast.success("Saved"); load(); };
  return (
    <div className="max-w-4xl">
      <h1 className="font-serif text-3xl font-bold">Settings</h1>
      <p className="text-charcoal-muted text-sm mt-1">Everything below is editable — no code changes needed.</p>
      <div className="mt-6 grid gap-4">
        <Card title="Notification Email" desc="Where booking, event, and quote notifications are sent.">
          <div className="flex gap-2">
            <input data-testid="settings-notif-email" value={s.notification_email || ""} onChange={(e) => setS({...s, notification_email: e.target.value})} className="flex-1 rounded-2xl border border-cream-alt px-4 py-2.5"/>
            <button onClick={() => save("notification_email", s.notification_email)} className="rounded-full bg-terracotta text-white px-5 py-2 font-semibold">Save</button>
          </div>
        </Card>
        <Card title="Square Payment Links" desc="Paste Square checkout links created in your Square account. Workshops have their own link field in the workshop editor.">
          <div className="grid gap-2">
            <label className="text-xs">Gift Cards
              <input data-testid="settings-square-gift" value={s.square_links?.gift_cards || ""} onChange={(e) => setS({...s, square_links: {...(s.square_links || {}), gift_cards: e.target.value}})} placeholder="https://square.link/u/…" className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-2.5"/>
            </label>
            <label className="text-xs">Patch Hat Bar
              <input data-testid="settings-square-patch" value={s.patch_hat_bar?.square_link || ""} onChange={(e) => setS({...s, patch_hat_bar: {...(s.patch_hat_bar || {}), square_link: e.target.value}})} placeholder="https://square.link/u/…" className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-2.5"/>
            </label>
            <button data-testid="settings-square-save" onClick={async () => { await api.put("/admin/settings/square_links", { value: s.square_links || {} }); await save("patch_hat_bar", s.patch_hat_bar || {}); }} className="justify-self-start rounded-full bg-terracotta text-white px-5 py-2 font-semibold">Save</button>
          </div>
        </Card>
        <Card title="Homepage Announcement" desc="Optional banner text.">
          <div className="flex gap-2">
            <input value={s.announcement || ""} onChange={(e) => setS({...s, announcement: e.target.value})} className="flex-1 rounded-2xl border border-cream-alt px-4 py-2.5"/>
            <button onClick={() => save("announcement", s.announcement)} className="rounded-full bg-terracotta text-white px-5 py-2 font-semibold">Save</button>
          </div>
        </Card>
        <Card title="Memberships" desc="Enable the memberships section on the site.">
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!s.memberships_enabled} onChange={(e) => { const v = e.target.checked; save("memberships_enabled", v); }}/> Enable memberships</label>
        </Card>

        <Card title="Sales Tax" desc="Applied automatically at checkout to taxable items. Stripe charges the same total shown to the customer (tax is one line item, never doubled).">
          <div className="grid gap-2 text-sm" data-testid="sales-tax-card">
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="tax-enabled" checked={!!s.sales_tax?.enabled} onChange={(e) => setS({...s, sales_tax: {...(s.sales_tax || {}), enabled: e.target.checked}})}/> Charge sales tax</label>
            <label className="text-xs">Rate (%)<input type="number" step="0.01" min="0" data-testid="tax-rate" value={s.sales_tax?.rate ?? 8} onChange={(e) => setS({...s, sales_tax: {...(s.sales_tax || {}), rate: Number(e.target.value)}})} className="mt-1 w-32 block rounded-2xl border border-cream-alt px-4 py-2"/></label>
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="tax-workshops" checked={s.sales_tax?.workshops_taxable !== false} onChange={(e) => setS({...s, sales_tax: {...(s.sales_tax || {}), workshops_taxable: e.target.checked}})}/> Workshop / rug price is taxable (individual workshops can opt out in the workshop editor)</label>
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="tax-addons" checked={s.sales_tax?.addons_taxable !== false} onChange={(e) => setS({...s, sales_tax: {...(s.sales_tax || {}), addons_taxable: e.target.checked}})}/> Add-ons are taxable</label>
            <button data-testid="tax-save" onClick={() => save("sales_tax", { enabled: !!s.sales_tax?.enabled, rate: Number(s.sales_tax?.rate ?? 8), workshops_taxable: s.sales_tax?.workshops_taxable !== false, addons_taxable: s.sales_tax?.addons_taxable !== false })} className="justify-self-start rounded-full bg-terracotta text-white px-5 py-2 font-semibold">Save</button>
          </div>
        </Card>

        <Card title="Booking Rules" desc="Min guests, deposit %, balance due window, extra-time fee.">
          <RulesEditor value={s.booking_rules || {}} onSave={(v) => save("booking_rules", v)}/>
        </Card>

        <Card title="Rug Sizes" desc="Add, edit, hide, reorder, or delete rug size options. Prices are per person. Set a sale price and enable it to offer a promo.">
          <ListEditor
            value={s.rug_sizes || []}
            onSave={(v) => save("rug_sizes", v)}
            fields={[
              { key: "label", label: "Label", type: "text" },
              { key: "dimensions", label: "Dimensions", type: "text" },
              { key: "price_per_person", label: "Regular price / person ($)", type: "number" },
              { key: "sale_price_per_person", label: "Sale price / person ($, optional)", type: "number" },
              { key: "sale_active", label: "Sale active", type: "boolean" },
            ]}
            testid="rug-sizes"
          />
        </Card>

        <Card title="Add-Ons" desc="Optional extras like Sip. Add, edit, hide, or delete. Prices per person.">
          <ListEditor
            value={s.booking_addons || []}
            onSave={(v) => save("booking_addons", v)}
            fields={[
              { key: "label", label: "Label", type: "text" },
              { key: "description", label: "Description", type: "text" },
              { key: "price_per_person", label: "Price / person ($)", type: "number" },
            ]}
            testid="addons"
          />
        </Card>

        <Card title="Mobile Event Config" desc="Editable pricing rules and disclaimers used on the Private Event request page for mobile events.">
          <div className="grid md:grid-cols-2 gap-3">
            <F label="Travel radius (miles)" v={s.mobile_config?.travel_radius_miles ?? 30} on={(v) => setS({...s, mobile_config: {...(s.mobile_config||{}), travel_radius_miles: Number(v)}})} type="number" name="mob-radius"/>
            <F label="Flat travel fee ($)" v={s.mobile_config?.travel_fee ?? 0} on={(v) => setS({...s, mobile_config: {...(s.mobile_config||{}), travel_fee: Number(v)}})} type="number" name="mob-travel"/>
            <F label="Mileage rate ($/mile)" v={s.mobile_config?.mileage_rate ?? 0} on={(v) => setS({...s, mobile_config: {...(s.mobile_config||{}), mileage_rate: Number(v)}})} type="number" name="mob-mileage"/>
            <F label="Setup fee ($)" v={s.mobile_config?.setup_fee ?? 0} on={(v) => setS({...s, mobile_config: {...(s.mobile_config||{}), setup_fee: Number(v)}})} type="number" name="mob-setup"/>
            <F label="Staffing fee ($)" v={s.mobile_config?.staffing_fee ?? 0} on={(v) => setS({...s, mobile_config: {...(s.mobile_config||{}), staffing_fee: Number(v)}})} type="number" name="mob-staff"/>
            <F label="Mobile event minimum guests" v={s.mobile_config?.min_guests ?? 10} on={(v) => setS({...s, mobile_config: {...(s.mobile_config||{}), min_guests: Number(v)}})} type="number" name="mob-min"/>
          </div>
          <div className="mt-3">
            <label className="text-xs">Mobile disclaimer (shown to customer)</label>
            <textarea data-testid="settings-mobile-disclaimer" rows={4} value={s.mobile_config?.disclaimer || ""} onChange={(e) => setS({...s, mobile_config: {...(s.mobile_config||{}), disclaimer: e.target.value}})} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
          </div>
          <div className="mt-3">
            <label className="text-xs">Out-of-area notice (shown when address may be outside service area)</label>
            <textarea data-testid="settings-mobile-outside" rows={3} value={s.mobile_config?.out_of_area_notice || ""} onChange={(e) => setS({...s, mobile_config: {...(s.mobile_config||{}), out_of_area_notice: e.target.value}})} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
          </div>
          <button onClick={() => save("mobile_config", s.mobile_config || {})} data-testid="save-mobile-config-btn" className="mt-2 rounded-full bg-terracotta text-white px-5 py-2 font-semibold">Save Mobile Config</button>
        </Card>

        <Card title="Sip Disclaimer" desc="Shown when the Sip (BYOB) add-on is selected. Customers must acknowledge before checkout.">
          <textarea data-testid="settings-sip-disclaimer" rows={6} value={s.tuft_sip_disclaimer || ""} onChange={(e) => setS({...s, tuft_sip_disclaimer: e.target.value})} className="w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
          <button onClick={() => save("tuft_sip_disclaimer", s.tuft_sip_disclaimer)} className="mt-2 rounded-full bg-terracotta text-white px-5 py-2 font-semibold">Save</button>
        </Card>

        <Card title="Event Service Config" desc="Which questions/options belong to each private-event service. Rug-flow services show rug sizes; others hide them.">
          <ListEditor
            value={s.event_service_config || []}
            onSave={(v) => save("event_service_config", v)}
            fields={[
              { key: "label", label: "Service label (shown to customer)", type: "text" },
              { key: "show_rug_sizes", label: "Show rug sizes", type: "boolean" },
              { key: "sip_enabled", label: "Allow Sip add-on", type: "boolean" },
            ]}
            testid="event-services"
          />
        </Card>

        <Card title="Policies" desc="Cancellation, refund, rescheduling, late arrival, design, booking, and any others. Required policies must be accepted before checkout.">
          <ListEditor
            value={s.policies || []}
            onSave={(v) => save("policies", v)}
            fields={[
              { key: "title", label: "Title", type: "text" },
              { key: "body", label: "Policy text", type: "textarea" },
              { key: "required", label: "Required", type: "boolean" },
            ]}
            testid="policies"
          />
        </Card>

        <Card title="Design Instructions" desc="Shown to customers on the booking wizard's Design step. Individual workshops/themes can override this.">
          <textarea data-testid="settings-design-instructions" rows={6} value={s.design_instructions || ""} onChange={(e) => setS({...s, design_instructions: e.target.value})} className="w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
          <button onClick={() => save("design_instructions", s.design_instructions)} className="mt-2 rounded-full bg-terracotta text-white px-5 py-2 font-semibold">Save</button>
        </Card>

        <Card title="Corporate Quote Pricing (legacy)" desc="Legacy calculator settings.">
          <QuoteCalcEditor value={s.quote_calc || {}} onSave={(v) => save("quote_calc", v)}/>
        </Card>
        <Card title="Custom Rug Pricing" desc="Per-square-foot rates and minimums.">
          <PricingEditor value={s.custom_rug_pricing || {}} onSave={(v) => save("custom_rug_pricing", v)}/>
        </Card>
      </div>
    </div>
  );
}

function RulesEditor({ value, onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [JSON.stringify(value)]);
  const fields = [
    ["min_guests", "Min guests for private event"],
    ["deposit_pct", "Deposit percentage (%)"],
    ["balance_due_days", "Balance due (days before event)"],
    ["extra_time_fee", "Extra time fee ($ per block)"],
    ["extra_time_block_minutes", "Extra time block (minutes)"],
  ];
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {fields.map(([k, label]) => (
        <label key={k} className="text-xs">{label}
          <input data-testid={`rules-${k}`} type="number" value={v[k] ?? ""} onChange={(e) => setV({...v, [k]: Number(e.target.value)})} className="mt-1 w-full rounded-2xl border border-cream-alt px-3 py-2"/>
        </label>
      ))}
      <button onClick={() => onSave(v)} data-testid="save-rules" className="md:col-span-2 rounded-full bg-terracotta text-white px-5 py-2 font-semibold">Save Rules</button>
    </div>
  );
}

function ListEditor({ value, onSave, fields, testid }) {
  const [items, setItems] = useState(value || []);
  useEffect(() => setItems(value || []), [JSON.stringify(value)]);
  const update = (i, key, val) => { const c = [...items]; c[i] = { ...c[i], [key]: val }; setItems(c); };
  const move = (i, dir) => { const c = [...items]; const j = i + dir; if (j < 0 || j >= c.length) return; [c[i], c[j]] = [c[j], c[i]]; c.forEach((it, idx) => it.order = idx); setItems(c); };
  const add = () => { const id = `item-${Date.now()}`; const empty = { id, order: items.length, hidden: false }; fields.forEach((f) => empty[f.key] = f.type === "number" ? 0 : f.type === "boolean" ? false : ""); setItems([...items, empty]); };
  const remove = (i) => { const c = [...items]; c.splice(i, 1); c.forEach((it, idx) => it.order = idx); setItems(c); };
  return (
    <div className="space-y-3" data-testid={`list-${testid}`}>
      {items.map((it, i) => (
        <div key={it.id || i} className="rounded-2xl bg-cream-alt/50 p-4" data-testid={`${testid}-item-${i}`}>
          <div className="grid md:grid-cols-2 gap-3">
            {fields.map((f) => (
              <label key={f.key} className="text-xs">{f.label}
                {f.type === "textarea" ? (
                  <textarea rows={2} value={it[f.key] ?? ""} onChange={(e) => update(i, f.key, e.target.value)} className="mt-1 w-full rounded-xl border border-cream-alt px-3 py-2 bg-white"/>
                ) : f.type === "boolean" ? (
                  <div className="mt-1"><input type="checkbox" checked={!!it[f.key]} onChange={(e) => update(i, f.key, e.target.checked)}/></div>
                ) : (
                  <input type={f.type} value={it[f.key] ?? ""} onChange={(e) => update(i, f.key, f.type === "number" ? Number(e.target.value) : e.target.value)} className="mt-1 w-full rounded-xl border border-cream-alt px-3 py-2 bg-white"/>
                )}
              </label>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs">
            <label className="flex items-center gap-1"><input type="checkbox" checked={!!it.hidden} onChange={(e) => update(i, "hidden", e.target.checked)}/> Hidden</label>
            <button onClick={() => move(i, -1)} className="px-2 py-1 rounded-full bg-white">↑</button>
            <button onClick={() => move(i, 1)} className="px-2 py-1 rounded-full bg-white">↓</button>
            <button onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`} className="ml-auto px-3 py-1 rounded-full bg-red-50 text-red-600 inline-flex items-center gap-1"><Trash2 size={12}/> Remove</button>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={add} data-testid={`${testid}-add`} className="rounded-full bg-cream-alt px-4 py-2 text-sm font-semibold inline-flex items-center gap-1"><Plus size={14}/> Add</button>
        <button onClick={() => onSave(items)} data-testid={`${testid}-save`} className="rounded-full bg-terracotta text-white px-5 py-2 text-sm font-semibold">Save Changes</button>
      </div>
    </div>
  );
}

function Card({ title, desc, children }) {
  return <div className="bg-white rounded-3xl p-4 sm:p-6 shadow-card min-w-0 overflow-x-auto"><div className="font-serif text-lg font-semibold">{title}</div><div className="text-charcoal-muted text-xs mb-4">{desc}</div>{children}</div>;
}

function QuoteCalcEditor({ value, onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [JSON.stringify(value)]);
  return (
    <div className="grid grid-cols-2 gap-3">
      {["base_per_person", "min_group", "corporate_multiplier", "travel_per_mile"].map((k) => (
        <label key={k} className="text-xs">{k}
          <input type="number" step="0.01" value={v[k] ?? ""} onChange={(e) => setV({...v, [k]: Number(e.target.value)})} className="mt-1 w-full rounded-2xl border border-cream-alt px-3 py-2"/>
        </label>
      ))}
      <button onClick={() => onSave(v)} className="col-span-2 rounded-full bg-terracotta text-white px-5 py-2 font-semibold">Save</button>
    </div>
  );
}
function PricingEditor({ value, onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [JSON.stringify(value)]);
  return (
    <div className="grid grid-cols-2 gap-3">
      {["per_sq_ft", "complexity_low", "complexity_medium", "complexity_high", "minimum"].map((k) => (
        <label key={k} className="text-xs">{k}
          <input type="number" step="0.01" value={v[k] ?? ""} onChange={(e) => setV({...v, [k]: Number(e.target.value)})} className="mt-1 w-full rounded-2xl border border-cream-alt px-3 py-2"/>
        </label>
      ))}
      <button onClick={() => onSave(v)} className="col-span-2 rounded-full bg-terracotta text-white px-5 py-2 font-semibold">Save</button>
    </div>
  );
}

function F({ label, v, on, type = "text", req, name }) {
  return <label className="text-xs">{label}<input data-testid={name} required={req} type={type} value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full rounded-2xl border border-cream-alt px-3 py-2"/></label>;
}
function Sel({ label, v, on, opts }) {
  return <label className="text-xs">{label}<select value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full rounded-2xl border border-cream-alt px-3 py-2 bg-white">{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select></label>;
}
